import React from "react";

export const productSliderTokens = {
  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  colors: {
    text: "#111111",
    textSoft: "rgba(17, 17, 17, 0.72)",
    textMuted: "rgba(17, 17, 17, 0.56)",
    border: "rgba(17, 17, 17, 0.10)",
    panel: "#ffffff",
    panelAlt: "#fafafa",
    track: "#f4f4f5",
    shadow: "0 14px 38px rgba(0, 0, 0, 0.08)",
    shadowHover: "0 16px 42px rgba(0, 0, 0, 0.12)",
    buttonBg: "rgba(255, 255, 255, 0.96)",
    buttonBorder: "rgba(17, 17, 17, 0.08)",
    buttonText: "#111111",
    accent: "#111111",
  },
  radius: {
    section: 24,
    tile: 18,
    button: 999,
  },
  spacing: {
    sectionPadding: 24,
    headerGap: 10,
    tileGap: 16,
    contentGap: 10,
    cardPadding: 14,
  },
} as const;

export const productSliderStyles = {
  section: {
    width: "100%",
    position: "relative",
    boxSizing: "border-box" as const,
    fontFamily: productSliderTokens.fontFamily,
    color: productSliderTokens.colors.text,
  },
  headerWrap: {
    display: "grid",
    gap: 10,
    marginBottom: 18,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    lineHeight: 1.1,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
    color: productSliderTokens.colors.textMuted,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 700,
    letterSpacing: "-0.03em",
    color: productSliderTokens.colors.text,
  },
  description: {
    margin: 0,
    maxWidth: 760,
    fontSize: 15,
    lineHeight: 1.7,
    fontWeight: 400,
    color: productSliderTokens.colors.textSoft,
  },
  tileLink: {
    display: "flex",
    height: "100%",
    color: "inherit",
    textDecoration: "none",
  },
  tile: {
    display: "grid",
    gridTemplateRows: "minmax(220px, 220px) minmax(0, 1fr)",
    minHeight: 100,
    height: "100%",
    width: "100%",
    overflow: "hidden",
    borderRadius: productSliderTokens.radius.tile,
    border: `1px solid ${productSliderTokens.colors.border}`,
    background: productSliderTokens.colors.panel,
    boxShadow: productSliderTokens.colors.shadow,
  },
  imageWrap: {
    position: "relative" as const,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    background: productSliderTokens.colors.track,
  },
  image: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover" as const,
  },
  imageFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box" as const,
    textAlign: "center" as const,
    fontSize: 14,
    lineHeight: 1.5,
    color: productSliderTokens.colors.textMuted,
    background: "linear-gradient(180deg, #f8f8f8 0%, #efefef 100%)",
  },
  content: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    padding: 14,
    background: productSliderTokens.colors.panel,
    boxSizing: "border-box" as const,
  },
  contentTop: {
    display: "grid",
    gap: 10,
  },
  contentBottom: {
    marginTop: "auto",
    paddingTop: 12,
  },
  productName: {
    margin: 0,
    fontSize: 17,
    lineHeight: 1.25,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: productSliderTokens.colors.text,
  },
  productDescription: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 400,
    color: productSliderTokens.colors.textSoft,
  },
  pricingRow: {
    display: "grid",
    gap: 2,
    justifySelf:"flex-start",
    alignContent: "end" as const,
    minWidth: "100%",
    flex: "1 1 auto",
  },
  priceLabel: {
    margin: 0,
    fontSize: 11,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: productSliderTokens.colors.textMuted,
  },
  priceValue: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.2,
    fontWeight: 700,
    color: productSliderTokens.colors.text,
  },
  footerRow: {
    display: "flex",
    flexDirection:"column",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
  },
  ctaWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: 12,
    flex: "0 0 auto",
    whiteSpace: "nowrap" as const,
  },
  ctaText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: "0.02em",
    color: productSliderTokens.colors.accent,
  },
  iconButton: {
    width: 42,
    height: 42,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: productSliderTokens.radius.button,
    border: `1px solid ${productSliderTokens.colors.buttonBorder}`,
    background: productSliderTokens.colors.buttonBg,
    color: productSliderTokens.colors.buttonText,
    cursor: "pointer",
    boxShadow: "0 8px 22px rgba(0, 0, 0, 0.08)",
  },
  scrollWrap: {
    width: "100%",
    overflowX: "auto" as const,
    overflowY: "hidden" as const,
    paddingBottom: 8,
    WebkitOverflowScrolling: "touch" as const,
    scrollbarWidth: "thin" as const,
  },
  scrollTrack: {
    display: "flex",
    gap: 16,
    minWidth: "max-content",
  },
} satisfies Record<string, React.CSSProperties>;

export const responsiveCss = `
.product-slider-swiper .swiper-wrapper {
  align-items: stretch;
}
.product-slider-swiper .swiper-slide {
  height: auto;
  display: flex;
}
.product-slider-swiper .swiper-slide > * {
  width: 100%;
}
.product-slider-scroll-track {
  align-items: stretch;
}
.product-slider-scroll::-webkit-scrollbar {
  height: 10px;
}
.product-slider-scroll::-webkit-scrollbar-thumb {
  background: rgba(17, 17, 17, 0.18);
  border-radius: 999px;
}
.product-slider-scroll::-webkit-scrollbar-track {
  background: rgba(17, 17, 17, 0.05);
}
@media (max-width: 767px) {
  .product-slider-title {
    font-size: 24px !important;
  }
  .product-slider-card {
    min-width: 240px !important;
    width: 240px !important;
  }
  .product-slider-card-image {
    min-height: 190px !important;
    height: 190px !important;
  }
}
@media (min-width: 768px) and (max-width: 1023px) {
  .product-slider-card {
    min-width: 280px !important;
    width: 280px !important;
  }
}
@media (min-width: 1024px) {
  .product-slider-card {
    min-width: 300px !important;
    width: 300px !important;
  }
}
`;

export function formatProductPrice(price?: number | null, currency: string = "AUD") {
  if (price === null || price === undefined || price <= 0) return "Enquire";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(price);
}
